/**
 * FoundationHome -- Foundation module home (P33.1).
 *
 * Replaces the v0.4.0 `HelloFoundation` stub with a real module
 * home, mirroring P32.2's TwinHome shape:
 *
 *   ModuleRubric ("Framing - Foundation")
 *   h1 + 1-line positioning
 *   Catalogue stats signals strip
 *   3 INTENT-grouped tile sections:
 *     REGISTER   -- the catalogues operators MAINTAIN
 *     CATALOGUE  -- the reference views operators CONSULT
 *     WORKSPACE  -- the project-shaped work surfaces
 *
 * Foundation's rhythm is project-shaped (quarterly maintenance +
 * onboarding work) per docs/v2-platform-plan.md § 2. The signals
 * strip surfaces catalogue HEALTH (have I got the data?) rather
 * than LIVE STATE (Twin's job).
 *
 * Module-boundary rule (eslint): foundation/* may import from
 * foundation/* + shared/*. Same constraint as the rest of the
 * module.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { usePosture } from "shared/posture/usePosture";
import { ModuleRubric, Tile, TileSection } from "shared/layout/ModuleHomeTile";
import { ViewRail } from "shared/onboarding/ViewRail";

interface BasicRow {
  id: string;
}

/**
 * Catalogue stats. 4 list calls fire in parallel via separate
 * `useQuery` hooks so a slow endpoint doesn't block the others.
 * 5-minute `staleTime` because catalogue data doesn't move
 * minute-to-minute (project-shaped work, not real-time).
 */
function useFoundationStats() {
  // Round 9 E9 -- these three are the ADMIN registers, gated on
  // `require_opsres_or_admin`. The CRO and the accountable executive
  // cross this page to reach the declarations register they sign on,
  // and took a 403 on each of the three on the way past, second round.
  //
  // The counts are a signal, not the reason they are here. Declining to
  // ask is the honest repair: widening the admin register to every
  // ceremony signatory would hand edit-shaped reads to seats that do
  // not edit, and the estate is readable through the twin either way.
  const me = useMe();
  const mayRead = me.isSuccess && canRunOperations(me.data);
  // Stitching review 5.4 -- the IBS and MVB figures are the posture's
  // register (admitted services), the ones the board and home print.
  // They were the length of the admin list: every row, candidates and
  // removed included, and capped at the list's page of 100.
  const posture = usePosture({ enabled: mayRead });
  // The catalogue counts are the register's own total (X-Total-Count),
  // not the length of one page: 162 services read as 100.
  const services = useQuery({
    queryKey: ["foundation-home", "services-count"] as const,
    enabled: mayRead,
    queryFn: () => api.getList<BasicRow[]>("/v1/admin/foundation/services?limit=1"),
    staleTime: 5 * 60_000,
  });
  const vendors = useQuery({
    queryKey: ["foundation-home", "vendors-count"] as const,
    enabled: mayRead,
    queryFn: () => api.getList<BasicRow[]>("/v1/admin/foundation/vendors?limit=1"),
    staleTime: 5 * 60_000,
  });
  return {
    ibsCount: posture.data?.register_services?.count ?? null,
    mvbCount: posture.data?.mvb_impacted?.of ?? null,
    serviceCount: services.data?.total ?? null,
    vendorCount: vendors.data?.total ?? null,
    loading: mayRead && (posture.isLoading || services.isLoading || vendors.isLoading),
    // A count we were never allowed to fetch is not zero and not
    // loading; the strip renders the tiles without figures.
    mayRead,
  };
}

function CatalogueSignalsStrip() {
  const s = useFoundationStats();
  const { t } = useTranslation();
  return (
    <section
      data-testid="foundation-signals-strip"
      /* Round 9 E9 -- three states, not two. "ready" had come to mean
         both "the counts are in" and "we never asked", which is the
         same conflation this fix exists to remove one line below. */
      data-state={!s.mayRead ? "not-permitted" : s.loading ? "loading" : "ready"}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        {/* Round 9 E9 -- four dashes would read as "we looked and found
            nothing". We did not look. */}
        {!s.mayRead && (
          <span
            data-testid="foundation-signals-not-yours"
            className="text-xs text-ink-2"
          >
            {t("modules.foundation.home.signals.notYours")}
          </span>
        )}
        {s.mayRead && s.loading && (
          <span className="text-slate-500 text-xs">
            {t("modules.foundation.home.signals.loading")}
          </span>
        )}
        {s.mayRead && !s.loading && (
          <>
            <span data-testid="foundation-stat-ibs" className="text-slate-800">
              <strong className="tabular-nums">{s.ibsCount ?? "-"}</strong>{" "}
              {t("modules.foundation.home.signals.ibses")}
            </span>
            <span data-testid="foundation-stat-mvb" className="text-indigo-800">
              <strong className="tabular-nums">{s.mvbCount ?? "-"}</strong>{" "}
              {t("modules.foundation.home.signals.mvbFlagged")}
            </span>
            <span data-testid="foundation-stat-services" className="text-slate-800">
              <strong className="tabular-nums">{s.serviceCount ?? "-"}</strong>{" "}
              {t("modules.foundation.home.signals.services")}
            </span>
            <span data-testid="foundation-stat-vendors" className="text-slate-800">
              <strong className="tabular-nums">{s.vendorCount ?? "-"}</strong>{" "}
              {t("modules.foundation.home.signals.vendors")}
            </span>
          </>
        )}
      </div>
    </section>
  );
}

export function FoundationHome() {
  const me = useMe();
  const { t } = useTranslation();
  const isAdmin = me.data?.is_admin ?? false;
  // Round 16 G13 -- the registers the resilience desk keeps; offered to
  // a service owner, IBS groups answered 403.
  const mayOperate = canRunOperations(me.data);

  return (
    <article data-testid="foundation-home" className="max-w-6xl space-y-6">
      <ViewRail viewKey="foundation" />
      <header>
        <ModuleRubric>{t("modules.foundation.home.rubric")}</ModuleRubric>
        <h1
          data-testid="foundation-home-heading"
          className="mt-1 font-display text-2xl font-semibold text-slate-900"
        >
          {t("modules.foundation.home.heading")}
        </h1>
        <p
          data-testid="foundation-home-positioning"
          className="mt-1 text-sm text-slate-700 max-w-3xl"
        >
          {t("modules.foundation.home.positioning")}
        </p>
      </header>

      <CatalogueSignalsStrip />

      <TileSection
        heading={t("modules.foundation.home.sections.register")}
        testid="foundation-section-register"
      >
        {/* P48.1 (#585): catalogue CRUD tiles moved to /admin
            (AdminHome > Catalogue section, P42). Single tile here
            so operators with /foundation muscle memory land
            somewhere useful. */}
        <Tile
          to="/admin"
          name={t("modules.foundation.home.tiles.adminConsole.name")}
          desc={t("modules.foundation.home.tiles.adminConsole.desc")}
          testid="foundation-tile-admin-console"
          icon="console"
          visible={isAdmin}
        />
      </TileSection>

      <TileSection
        heading={t("modules.foundation.home.sections.catalogue")}
        testid="foundation-section-catalogue"
      >
        <Tile
          to="/twin/landscape/BIAN"
          name={t("modules.foundation.home.tiles.landscape.name")}
          desc={t("modules.foundation.home.tiles.landscape.desc")}
          testid="foundation-tile-landscape"
          icon="capability-map"
        />
        <Tile
          to="/twin/capmap"
          name={t("modules.foundation.home.tiles.capmap.name")}
          desc={t("modules.foundation.home.tiles.capmap.desc")}
          testid="foundation-tile-capmap"
          icon="capability-map"
        />
      </TileSection>

      <TileSection
        heading={t("modules.foundation.home.sections.workspace")}
        testid="foundation-section-workspace"
      >
        <Tile
          to="/foundation/mvb"
          name={t("modules.foundation.home.tiles.mvb.name")}
          desc={t("modules.foundation.home.tiles.mvb.desc")}
          testid="foundation-tile-mvb"
          icon="mvb"
        />
        <Tile
          to="/foundation/admin/data-assets"
          name={t("modules.foundation.home.tiles.dataAssets.name")}
          desc={t("modules.foundation.home.tiles.dataAssets.desc")}
          testid="foundation-tile-data-assets"
          icon="document-locker"
        />
        <Tile
          to="/foundation/admin/ibs-groups"
          name={t("modules.foundation.home.tiles.ibsGroups.name")}
          desc={t("modules.foundation.home.tiles.ibsGroups.desc")}
          testid="foundation-tile-ibs-groups"
          visible={mayOperate}
          icon="ibs"
        />
        <Tile
          to="/foundation/admin/technology-lifecycle"
          name={t("modules.foundation.home.tiles.technologyLifecycle.name")}
          desc={t("modules.foundation.home.tiles.technologyLifecycle.desc")}
          testid="foundation-tile-technology-lifecycle"
          visible={mayOperate}
          icon="telemetry"
        />
        <Tile
          to="/foundation/admin/ea-import"
          name={t("modules.foundation.home.tiles.eaImport.name")}
          desc={t("modules.foundation.home.tiles.eaImport.desc")}
          testid="foundation-tile-ea-import"
          visible={mayOperate}
          icon="capability-map"
        />
        <Tile
          to="/foundation/admin/floor-list"
          name={t("modules.foundation.home.tiles.floorList.name")}
          desc={t("modules.foundation.home.tiles.floorList.desc")}
          testid="foundation-tile-floor-list"
          visible={mayOperate}
          icon="obligation"
        />
        <Tile
          to="/foundation/admin/funding-resources"
          name={t("modules.foundation.home.tiles.fundingResources.name")}
          desc={t("modules.foundation.home.tiles.fundingResources.desc")}
          testid="foundation-tile-funding-resources"
          visible={mayOperate}
          icon="blast-radius"
        />
        <Tile
          to="/foundation/admin/regulatory-commitments"
          name={t("modules.foundation.home.tiles.regulatoryCommitments.name")}
          desc={t("modules.foundation.home.tiles.regulatoryCommitments.desc")}
          testid="foundation-tile-regulatory-commitments"
          icon="obligation"
        />
        <Tile
          to="/foundation/admin/risk-appetite-statements"
          name={t("modules.foundation.home.tiles.riskAppetite.name")}
          desc={t("modules.foundation.home.tiles.riskAppetite.desc")}
          testid="foundation-tile-risk-appetite"
          icon="tolerance-clock"
        />
        <Tile
          to="/foundation/admin/outsourcing-contracts"
          name={t("modules.foundation.home.tiles.outsourcingContracts.name")}
          desc={t("modules.foundation.home.tiles.outsourcingContracts.desc")}
          testid="foundation-tile-outsourcing-contracts"
          icon="blast-radius"
        />
      </TileSection>
    </article>
  );
}
